import { useList, useOne } from "@refinedev/core";
import { useParams, useSearchParams } from "react-router-dom";
import Editor from "../editor";
import { LoadingButton } from "@mui/lab";
import { Job, SheetData } from "../playground/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import useGlobalStore from "../../store";
import { useSafeApiCall } from "../../utils/apiHelper";
import { getSheetData } from "../../api/sheet";
import { keywordsSearch } from "../../api/phrasebook";

export const JobView: React.FC = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const safeApiCall = useSafeApiCall();

  const { setShowGDriveLoginError } = useGlobalStore();

  const [sheetData, setSheetData] = useState<SheetData | null>(null);

  // Get filter values from URL parameters
  const columnFilter = searchParams.get("column");
  const statusFilter = searchParams.get("status");

  // Get keyword search params from URL
  const keywordQuery = searchParams.get("keyword_query");
  const keywordScope = searchParams.get("keyword_scope") as
    | "batch"
    | "brand"
    | null;
  const keywordBatchId = searchParams.get("keyword_batch_id");

  // State for keyword search jobs
  const [keywordSearchJobs, setKeywordSearchJobs] = useState<Job[] | null>(
    null
  );

  const { data: jobData } = useOne({
    resource: "job",
    id: jobId,
  });

  const { data: batchData } = useOne({
    resource: "batch",
    id: jobData?.data?.batch_id,
    queryOptions: {
      enabled: !!jobData?.data?.batch_id,
    },
  });

  const { data: jobsData } = useList({
    resource: "job",
    meta: {
      params: {
        batch_id: batchData?.data?.id,
      },
    },
    queryOptions: {
      enabled: !!batchData?.data?.id,
    },
  });

  const batchId = batchData?.data?.id as string;
  const jobs = jobsData?.data as Job[];

  const refreshSheetData = useCallback(async () => {
    await safeApiCall(
      getSheetData,
      [batchId],
      setSheetData,
      { errorMessage: "Error fetching sheet data" },
      {
        onFail: () => {
          if (
            !batchData?.data?.client_owned &&
            batchData?.data?.gdrive_folder_id
          ) {
            setShowGDriveLoginError(true);
          }
        },
      }
    );
  }, [
    batchId,
    safeApiCall,
    setShowGDriveLoginError,
    batchData?.data?.client_owned,
    batchData?.data?.gdrive_folder_id,
  ]);

  useEffect(() => {
    if (!batchId) return;

    refreshSheetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // Fetch keyword search jobs when keyword params are present
  useEffect(() => {
    if (!keywordQuery || !keywordScope || !keywordBatchId) {
      setKeywordSearchJobs(null);
      return;
    }

    const fetchKeywordJobs = async () => {
      try {
        const response = await keywordsSearch({
          batchId: keywordBatchId,
          query: keywordQuery,
          scope: keywordScope,
        });
        const data = response.data.data;
        if (Array.isArray(data)) {
          setKeywordSearchJobs(data);
        } else {
          setKeywordSearchJobs([]);
        }
      } catch (e) {
        console.error("Error fetching keyword search jobs:", e);
        setKeywordSearchJobs([]);
      }
    };

    fetchKeywordJobs();
  }, [keywordQuery, keywordScope, keywordBatchId]);

  // Determine if we're in keyword search mode
  const isKeywordSearchMode = !!(keywordQuery && keywordScope && keywordBatchId);

  // Build query string - keyword search and normal filters are mutually exclusive
  const params = new URLSearchParams();
  if (isKeywordSearchMode) {
    // Only add keyword search params
    params.set("keyword_query", keywordQuery);
    params.set("keyword_scope", keywordScope);
    params.set("keyword_batch_id", keywordBatchId);
  } else {
    // Only add normal filters
    if (columnFilter) params.set("column", columnFilter);
    if (statusFilter) params.set("status", statusFilter);
  }
  const queryString = params.toString();

  let previousJobUrl: string | undefined;
  let nextJobUrl: string | undefined;
  const backToBatchUrl = `/batch/${isKeywordSearchMode ? keywordBatchId : batchId}${
    isKeywordSearchMode ? "" : queryString ? `?${queryString}` : ""
  }`;

  // Apply filters to jobs and group if needed
  const filteredAndGroupedJobs = useMemo(() => {
    if (!jobs) {
      return {
        groups: null,
        jobs: [],
      };
    }

    // First filter jobs based on search, status, and assignment filters
    const filtered = jobs.filter((job) => {
      // Apply status filter
      const matchesStatus = !statusFilter || job.status === statusFilter;

      return matchesStatus;
    });

    // If no column is selected, just return the filtered jobs
    if (!columnFilter || !sheetData) {
      return {
        groups: null,
        jobs: filtered,
      };
    }

    // Group the jobs by the selected column
    const groups: Record<string, Job[]> = {};

    filtered.forEach((job) => {
      let groupKey = "Non specificato";

      // Handle special columns that come directly from the job object
      if (columnFilter === "garment_type") {
        // Use job's garment_type property
        groupKey = job.garment_type || "Non specificato";
      }
      // Handle sheet data columns
      else if (sheetData && sheetData.items.length > 0) {
        // Since the SKU is always the first field, use that for matching
        const sheetItems = sheetData.items.filter((item) => {
          // Get the first property (which is the SKU field, whatever it's called)
          const skuField = Object.keys(item)[0];
          // String coercion: pandas parses numeric-only SKU columns as floats,
          // but job.master_id is always a string. Compare as strings to match.
          return String(item[skuField]).trim() === String(job.master_id).trim();
        });

        if (sheetItems && sheetItems.length > 0) {
          // Use the first sheet item's value for the selected column as the group key
          if (columnFilter in sheetItems[0]) {
            groupKey =
              sheetItems[0][columnFilter]?.toString() || "Non specificato";
          } else {
            // Column not found in the item
            groupKey = "Non specificato";
          }
        }
      }

      // Add job to the appropriate group
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(job);
    });

    return {
      groups,
      jobs: filtered,
    };
  }, [jobs, statusFilter, columnFilter, sheetData]);

  let filteredJobs = filteredAndGroupedJobs.jobs;

  if (filteredAndGroupedJobs.groups) {
    filteredJobs = Object.values(filteredAndGroupedJobs.groups).flat();
  }

  // Use keyword search jobs if in keyword search mode, otherwise use filtered batch jobs
  const jobsForNavigation = isKeywordSearchMode ? keywordSearchJobs : filteredJobs;

  if (jobsForNavigation && jobsForNavigation.length > 0) {
    // Find the current job's index
    const currentIndex = jobsForNavigation.findIndex((job) => job.id === jobId);

    if (currentIndex !== -1) {
      // Get previous job if it exists
      if (currentIndex > 0) {
        previousJobUrl = `/job/${jobsForNavigation[currentIndex - 1].id}${
          queryString ? `?${queryString}` : ""
        }`;
      }

      // Get next job if it exists
      if (currentIndex < jobsForNavigation.length - 1) {
        nextJobUrl = `/job/${jobsForNavigation[currentIndex + 1].id}${
          queryString ? `?${queryString}` : ""
        }`;
      }
    }
  }

  if (!batchId || !jobId) {
    return <LoadingButton loading />;
  }

  return (
    <Editor
      batchId={batchId}
      jobId={jobId}
      backToBatchUrl={backToBatchUrl}
      previousJobUrl={previousJobUrl}
      nextJobUrl={nextJobUrl}
      sheetData={sheetData || undefined}
    />
  );
};
